import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function ContactSection() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      const body = new URLSearchParams();
      body.append('name', formData.name);
      body.append('email', formData.email);
      if (formData.phone) body.append('phone', formData.phone);
      body.append('subject', formData.subject);
      body.append('message', formData.message);
      const response = await fetch('https://readdy.ai/api/form/d77ao0iqo7glkto3e5ag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
        setTimeout(() => setSubmitted(false), 6000);
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } catch {
      setSubmitError('Network error. Please check your connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="w-full px-4 sm:px-6 lg:px-12 xl:px-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left */}
          <div>
            <p className="text-amber-600 text-sm font-semibold uppercase tracking-widest mb-2">Get In Touch</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-stone-900 mb-5">Let's Connect</h2>
            <p className="text-stone-400 text-base leading-relaxed mb-10">
              Have questions about our products or interested in partnership opportunities? We'd love to hear from you.
            </p>
            <div className="space-y-5 mb-10">
              {[
                { icon: 'ri-map-pin-line', label: 'Address', value: 'Faasleyfoods Pvt. Ltd., India' },
                { icon: 'ri-mail-line', label: 'Email', value: 'pari211284@gmail.com' },
                { icon: 'ri-phone-line', label: 'Phone', value: '+91 8800889959' },
                { icon: 'ri-time-line', label: 'Hours', value: 'Mon–Sat: 9AM – 6PM IST' },
              ].map((info) => (
                <div key={info.label} className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-stone-50 rounded-xl flex-shrink-0">
                    <i className={`${info.icon} text-amber-500`}></i>
                  </div>
                  <div>
                    <p className="text-stone-400 text-xs">{info.label}</p>
                    <p className="text-stone-800 font-medium text-sm">{info.value}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-3">
              {[
                { icon: 'ri-instagram-line', label: 'Instagram', href: '#' },
                { icon: 'ri-facebook-circle-line', label: 'Facebook', href: '#' },
                { icon: 'ri-whatsapp-line', label: 'WhatsApp', href: 'https://wa.me/918800889959' },
                { icon: 'ri-linkedin-box-line', label: 'LinkedIn', href: '#' },
              ].map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="nofollow noreferrer" aria-label={s.label}
                  className="w-10 h-10 rounded-full border border-stone-200 flex items-center justify-center text-stone-400 hover:bg-stone-900 hover:border-stone-900 hover:text-white transition-all cursor-pointer">
                  <i className={`${s.icon} text-base`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-stone-50 rounded-3xl p-7 md:p-9">
            <h3 className="font-display text-xl font-bold text-stone-900 mb-6">Send us a Message</h3>
            {submitted ? (
              <div className="text-center py-10">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-green-600 text-2xl"></i>
                </div>
                <h4 className="text-stone-900 font-bold text-lg mb-2">Message Sent!</h4>
                <p className="text-stone-400 text-sm">We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 mb-1.5 uppercase tracking-wider">Full Name *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required
                      className="w-full px-4 py-3 bg-white border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 transition-colors"
                      placeholder="Your name" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 mb-1.5 uppercase tracking-wider">Email *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} required
                      className="w-full px-4 py-3 bg-white border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 transition-colors"
                      placeholder="your@email.com" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 mb-1.5 uppercase tracking-wider">Phone</label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 transition-colors"
                      placeholder="+91 XXXXX XXXXX" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-stone-500 mb-1.5 uppercase tracking-wider">Subject *</label>
                    <select name="subject" value={formData.subject} onChange={handleChange} required
                      className="w-full px-4 py-3 bg-white border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 transition-colors cursor-pointer">
                      <option value="">Select subject</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <option value="Product Query">Product Query</option>
                      <option value="Investment Opportunity">Investment Opportunity</option>
                      <option value="Partnership">Partnership</option>
                      <option value="Wholesale / Bulk Order">Wholesale / Bulk Order</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-stone-500 mb-1.5 uppercase tracking-wider">Message *</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} maxLength={500}
                    className="w-full px-4 py-3 bg-white border border-stone-200 rounded-xl text-sm focus:outline-none focus:border-amber-400 transition-colors resize-none"
                    placeholder="Tell us how we can help..." />
                  <p className="text-stone-400 text-xs mt-1 text-right">{formData.message.length}/500</p>
                </div>
                {submitError && (
                  <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-500 text-sm"></i>
                    <p className="text-red-600 text-xs">{submitError}</p>
                  </div>
                )}
                <button type="submit" disabled={isSubmitting || formData.message.length > 500}
                  className="w-full bg-stone-900 hover:bg-stone-700 text-white font-semibold py-4 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-60 cursor-pointer whitespace-nowrap">
                  {isSubmitting ? <><i className="ri-loader-4-line animate-spin"></i>Sending...</> : <><i className="ri-send-plane-line"></i>Send Message</>}
                </button>
              </form>
            )}
          </div>
        </div>

        <div className="text-center mt-12">
          <Link to="/contact" className="inline-flex items-center gap-2 text-amber-600 font-semibold hover:underline text-sm">
            Visit full contact page <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
